import { useEffect, useRef } from 'react';
import { useCurrentPatientStore, type CurrentPatient } from '../stores/currentPatientStore';

/**
 * "Last, First" — matches the billing/NEXI/queue convention
 * (`formatPatientNameLastFirst`), null when there is nothing to show rather
 * than inventing a placeholder.
 */
export function toDisplayName(last?: string | null, first?: string | null): string | null {
  const l = (last ?? '').trim();
  const f = (first ?? '').trim();
  if (!l && !f) return null;
  return l && f ? `${l}, ${f}` : l || f;
}

/**
 * PRX-85: sets the top bar's current patient while the caller is mounted AND
 * `patient` is non-null/non-undefined, restoring whatever the store held
 * before on unmount — or on the transition back to `null`/`undefined` while
 * the caller stays mounted (a dialog closing without unmounting: pass
 * `open ? patient : undefined`).
 *
 * `undefined` means "no data yet / not applicable" and is always a no-op —
 * it must never clear a patient this hook did not itself set.
 */
export function useCurrentPatientContext(patient: CurrentPatient | null | undefined): void {
  const setPatient = useCurrentPatientStore((s) => s.setPatient);
  // What this hook replaced, so it can be put back — not what THIS hook set.
  const previousRef = useRef<CurrentPatient | null>(null);
  // Whether this hook currently has a patient in the store to restore from.
  const isSetRef = useRef(false);
  const key = patient ? `${patient.compositeId}|${patient.displayName}` : '';

  useEffect(() => {
    if (!patient) {
      // The dialog-close case: `patient` went from set to undefined/null
      // without the component unmounting. Restore explicitly rather than
      // relying only on the cleanup below running on the next deps change.
      if (isSetRef.current) {
        setPatient(previousRef.current);
        isSetRef.current = false;
      }
      return;
    }

    previousRef.current = useCurrentPatientStore.getState().patient;
    isSetRef.current = true;
    setPatient(patient);

    return () => {
      setPatient(previousRef.current);
      isSetRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
